import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { SchoolsPageShimmer } from "../components/ui/page-shimmers";
import { TruncatedTableCell } from "../components/ui/truncated-text";
import { SearchFilter } from "../components/SearchFilter";
import { CampusCard } from "../components/CampusCard";
import { CampusForm, CampusFormData } from "../components/CampusForm";
import CampusBoundary from "../components/CampusBoundary/CampusBoundary";
import { ConfirmationDialog } from "../components/ConfirmationDialog";
import { UserManagementDialog } from "../components/UserManagementDialog";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetClose,
  SheetDescription,
} from "../components/ui/sheet";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { Badge } from "../components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { schoolsAPI, campusAPI, campusSyncAPI } from "../api";
import { School, Campus } from "../types";
import { useAuthStore } from "../store/authStore";
import {
  PlusIcon,
  PencilIcon,
  GraduationCap,
  CheckCircle,
  XCircle,
  Building2,
  Users,
  Trash2,
  MoreHorizontal,
  AlertCircle,
  Loader2,
  RefreshCw,
} from "lucide-react";
import { StatCard } from "../components/common/StatCard";

type ApiError = Error & { response?: { data?: { message?: string } } };

interface CampusBoundaryData {
  type?: "Feature";
  geometry: {
    type: string;
    coordinates: number[][][];
  };
  properties?: Record<string, unknown>;
}

interface CampusCreateData {
  campus_name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone_number: string;
  is_active: boolean;
  time_zone: string;
  dawn_to_dusk: number[];
  school_id: string;
  seed_default_content: boolean;
  boundary_coordinates?: CampusBoundaryData;
  coordinates?: {
    type: "Polygon";
    coordinates: number[][][];
  };
}

export function SchoolsPageImproved() {
  // State Management
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [campusSearchTerm, setCampusSearchTerm] = useState("");
  const [selectedTab, setSelectedTab] = useState("schools");
  const [campusAttachmentError, setCampusAttachmentError] = useState<
    string | null
  >(null);

  // Dialog States
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  // The school create flow runs as an imperative sequence rather than through a
  // mutation, so it needs its own in-flight flag. Without one the submit button
  // never disables and a double click creates the school twice.
  const [isCreatingSchool, setIsCreatingSchool] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isManageCampusesDialogOpen, setIsManageCampusesDialogOpen] =
    useState(false);
  const [isCreateCampusDialogOpen, setIsCreateCampusDialogOpen] =
    useState(false);
  const [isEditCampusDialogOpen, setIsEditCampusDialogOpen] = useState(false);
  const [isUserManagementDialogOpen, setIsUserManagementDialogOpen] =
    useState(false);

  // Selected campus for editing
  const [selectedCampus, setSelectedCampus] = useState<Campus | null>(null);

  // Syncing campus ID (tracks which campus is currently syncing)
  const [syncingCampusId, setSyncingCampusId] = useState<string | null>(null);

  // Confirmation Dialog State
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
    variant?: "default" | "destructive";
  }>({
    isOpen: false,
    title: "",
    description: "",
    onConfirm: () => {},
  });

  // Selection States
  const [selectedSchool, setSelectedSchool] = useState<School | null>(null);
  const [userManagementEntity, setUserManagementEntity] = useState<{
    type: "school" | "campus";
    id: string;
    name: string;
  } | null>(null);

  // Form States
  const initialFormData = {
    school_name: "",
    display_name: "",
    email_domain: "",
    is_active: true,
    // Campus fields - always create campus with school
    createCampusWithSchool: true,
    campusName: "",
    campusAddress: "",
    campusCity: "",
    campusState: "",
    campusZip: "",
    campusPhone: "",
    campusTimeZone: "America/New_York",
    // Whether the new campus gets its Walky welcome space / event / idea. The
    // real default comes from the backend settings; this is the fallback for
    // the moment before that query resolves.
    seedDefaultContent: true,
  };

  const [formData, setFormData] = useState(initialFormData);

  // Campus boundary state
  const [campusBoundaryData, setCampusBoundaryData] =
    useState<CampusBoundaryData | null>(null);

  const [notification, setNotification] = useState<{
    show: boolean;
    message: string;
    type: "success" | "error" | "info";
  }>({
    show: false,
    message: "",
    type: "success",
  });

  const { isAuthenticated } = useAuthStore();
  const queryClient = useQueryClient();

  // API Queries
  const {
    data: schoolsData,
    isLoading: isLoadingSchools,
    refetch: refetchSchools,
  } = useQuery({
    queryKey: ["schools"],
    queryFn: schoolsAPI.getAllSchools,
    enabled: isAuthenticated,
  });

  const {
    data: campusesData,
    isLoading: isLoadingCampuses,
    refetch: refetchCampuses,
  } = useQuery({
    queryKey: ["campuses"],
    queryFn: campusAPI.getAllCampuses,
    enabled: isAuthenticated,
  });

  const { data: schoolCampusesData, isLoading: isLoadingSchoolCampuses } =
    useQuery({
      queryKey: ["school-campuses", selectedSchool?._id],
      queryFn: () =>
        selectedSchool
          ? schoolsAPI.getSchoolCampuses(selectedSchool._id)
          : null,
      enabled:
        isAuthenticated && !!selectedSchool && isManageCampusesDialogOpen,
    });

  // Mutations
  //
  // There is deliberately no createSchoolMutation: creating a school is a
  // three-call sequence (school -> campus -> attach) driven imperatively by the
  // form's onSubmit, tracked with `isCreatingSchool`. A mutation used to exist
  // here but nothing ever called it, so its isPending was permanently false and
  // the submit button never disabled.

  const updateSchoolMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<School> }) =>
      schoolsAPI.updateSchool(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schools"] });
      setIsEditDialogOpen(false);
      showNotification("School updated successfully", "success");
    },
    onError: (error: ApiError) => {
      showNotification(
        error.response?.data?.message || "Failed to update school",
        "error",
      );
    },
  });

  const deleteSchoolMutation = useMutation({
    mutationFn: schoolsAPI.deleteSchool,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schools"] });
      showNotification("School deleted successfully", "success");
    },
    onError: (error: ApiError) => {
      showNotification(
        error.response?.data?.message || "Failed to delete school",
        "error",
      );
    },
  });

  const createCampusMutation = useMutation({
    mutationFn: campusAPI.createCampus,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campuses"] });
      // Also refresh the per-school campus list, otherwise a campus created
      // from "Manage Campuses" does not appear until a reload.
      queryClient.invalidateQueries({ queryKey: ["school-campuses"] });
      queryClient.invalidateQueries({ queryKey: ["schools"] });
      setIsCreateCampusDialogOpen(false);
      showNotification("Campus created successfully", "success");
    },
    onError: (error: ApiError) => {
      showNotification(
        error.response?.data?.message || "Failed to create campus",
        "error",
      );
    },
  });

  const deleteCampusMutation = useMutation({
    mutationFn: campusAPI.deleteCampus,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campuses"] });
      queryClient.invalidateQueries({ queryKey: ["school-campuses"] });
      showNotification("Campus deleted successfully", "success");
    },
    onError: (error: ApiError) => {
      showNotification(
        error.response?.data?.message || "Failed to delete campus",
        "error",
      );
    },
  });

  const updateCampusMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Campus> }) =>
      campusAPI.updateCampus(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campuses"] });
      queryClient.invalidateQueries({ queryKey: ["school-campuses"] });
      setIsEditCampusDialogOpen(false);
      setSelectedCampus(null);
      showNotification("Campus updated successfully", "success");
    },
    onError: (error: ApiError) => {
      showNotification(
        error.response?.data?.message || "Failed to update campus",
        "error",
      );
    },
  });

  const syncCampusMutation = useMutation({
    mutationFn: (campusId: string) => campusSyncAPI.syncCampus(campusId),
    onMutate: (campusId) => {
      setSyncingCampusId(campusId);
    },
    onSuccess: (data) => {
      setSyncingCampusId(null);
      showNotification(
        `Sync completed: ${data.places_added} added, ${data.places_updated} updated, ${data.places_removed} removed`,
        "success",
      );
    },
    onError: (error: ApiError) => {
      setSyncingCampusId(null);
      showNotification(
        error.response?.data?.message || "Failed to sync places",
        "error",
      );
    },
  });

  // Helper Functions
  const showNotification = (
    message: string,
    type: "success" | "error" | "info",
  ) => {
    setNotification({ show: true, message, type });
    setTimeout(
      () => setNotification({ show: false, message: "", type: "success" }),
      5000,
    );
  };

  const resetFormData = () => {
    setFormData(initialFormData);
    setCampusBoundaryData(null);
  };

  const handleDeleteSchool = (school: School) => {
    setConfirmDialog({
      isOpen: true,
      title: "Delete School",
      description: `Are you sure you want to delete "${school.school_name}"? This action cannot be undone.`,
      variant: "destructive",
      onConfirm: () => {
        deleteSchoolMutation.mutate(school._id);
        setConfirmDialog({ ...confirmDialog, isOpen: false });
      },
    });
  };

  const handleDeleteCampus = (campus: Campus) => {
    setConfirmDialog({
      isOpen: true,
      title: "Delete Campus",
      description: `Are you sure you want to delete "${campus.campus_name}"? This action cannot be undone.`,
      variant: "destructive",
      onConfirm: () => {
        deleteCampusMutation.mutate(campus._id);
        setConfirmDialog({ ...confirmDialog, isOpen: false });
      },
    });
  };

  const handleEditCampus = (campus: Campus) => {
    setSelectedCampus(campus);
    setIsEditCampusDialogOpen(true);
  };

  const handleSyncPlaces = (campusId: string) => {
    syncCampusMutation.mutate(campusId);
  };

  const handleDetachCampus = (campusId: string, campusName: string) => {
    if (!selectedSchool) return;

    setConfirmDialog({
      isOpen: true,
      title: "Detach Campus",
      description: `Are you sure you want to detach "${campusName}" from this school?`,
      onConfirm: async () => {
        try {
          // Use the school's detach endpoint
          await schoolsAPI.detachCampusFromSchool(selectedSchool._id, campusId);

          // Refresh all data
          queryClient.invalidateQueries({ queryKey: ["campuses"] });
          queryClient.invalidateQueries({ queryKey: ["school-campuses"] });
          queryClient.invalidateQueries({ queryKey: ["schools"] });
          showNotification("Campus detached successfully", "success");
        } catch (err) {
          const error = err as ApiError;
          showNotification(
            error.response?.data?.message || "Failed to detach campus",
            "error",
          );
        }
        setConfirmDialog({ ...confirmDialog, isOpen: false });
      },
    });
  };

  // Data Processing
  const schools = useMemo(() => schoolsData || [], [schoolsData]);
  const allCampuses = useMemo(
    () =>
      Array.isArray(campusesData) ? campusesData : campusesData?.data || [],
    [campusesData],
  );
  const schoolCampuses = useMemo(() => {
    // Only use campuses from the school's API endpoint (campus list stored in school document)
    // Don't check campus.school_id as a campus can be attached to multiple schools
    const apiCampuses =
      schoolCampusesData?.campuses || schoolCampusesData || [];

    console.log("School campuses from API:", {
      schoolId: selectedSchool?._id,
      campusCount: apiCampuses.length,
      campuses: apiCampuses,
    });

    return apiCampuses;
  }, [schoolCampusesData, selectedSchool]);

  const availableCampuses = useMemo(() => {
    if (!selectedSchool) return [];

    // "Available" has to mean attachable. Listing every campus not attached to
    // *this* school also offered campuses owned by other schools, and the
    // backend rejects those with 409 "already attached to another school" — so
    // the list was full of options that could never work.
    return allCampuses.filter((campus: Campus) => {
      const isAlreadyAttached = schoolCampuses.some(
        (sc: Campus) => sc._id === campus._id,
      );
      if (isAlreadyAttached) return false;

      const ownerId =
        typeof campus.school_id === "string"
          ? campus.school_id
          : campus.school_id?._id;

      // Unowned, or owned by this school but missing from its campuses[] (a
      // half-broken link the attach endpoint knows how to repair).
      return !ownerId || ownerId === selectedSchool._id;
    });
  }, [allCampuses, selectedSchool, schoolCampuses]);

  // Filtering Logic
  const filteredSchools = useMemo(() => {
    return schools.filter((school: School) => {
      const matchesSearch =
        school.school_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        school.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        school.email_domain.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" && school.is_active) ||
        (statusFilter === "inactive" && !school.is_active);

      return matchesSearch && matchesStatus;
    });
  }, [schools, searchTerm, statusFilter]);

  const filteredCampuses = useMemo(() => {
    return allCampuses.filter(
      (campus: Campus) =>
        campus.campus_name
          .toLowerCase()
          .includes(campusSearchTerm.toLowerCase()) ||
        campus.city?.toLowerCase().includes(campusSearchTerm.toLowerCase()) ||
        campus.state?.toLowerCase().includes(campusSearchTerm.toLowerCase()),
    );
  }, [allCampuses, campusSearchTerm]);

  const filteredAvailableCampuses = useMemo(() => {
    if (!campusSearchTerm) return availableCampuses;

    return availableCampuses.filter(
      (campus: Campus) =>
        campus.campus_name
          .toLowerCase()
          .includes(campusSearchTerm.toLowerCase()) ||
        campus.city?.toLowerCase().includes(campusSearchTerm.toLowerCase()) ||
        campus.state?.toLowerCase().includes(campusSearchTerm.toLowerCase()),
    );
  }, [availableCampuses, campusSearchTerm]);

  const filteredSchoolCampuses = useMemo(() => {
    return schoolCampuses.filter(
      (campus: Campus) =>
        campus.campus_name
          .toLowerCase()
          .includes(campusSearchTerm.toLowerCase()) ||
        campus.city?.toLowerCase().includes(campusSearchTerm.toLowerCase()),
    );
  }, [schoolCampuses, campusSearchTerm]);

  // Statistics
  const stats = useMemo(() => {
    return {
      totalSchools: schools.length,
      activeSchools: schools.filter((s: School) => s.is_active).length,
      inactiveSchools: schools.filter((s: School) => !s.is_active).length,
      totalCampuses: allCampuses.length,
      activeCampuses: allCampuses.filter((c: Campus) => c.is_active).length,
    };
  }, [schools, allCampuses]);

  const activeFiltersCount = statusFilter !== "all" ? 1 : 0;

  if (isLoadingSchools || isLoadingCampuses) {
    return (
      <DashboardLayout>
        <SchoolsPageShimmer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Notification */}
        {notification.show && (
          <Alert
            className={`
              ${
                notification.type === "error"
                  ? "border-destructive bg-destructive/10"
                  : ""
              }
              ${
                notification.type === "success"
                  ? "border-green-500 bg-green-500/10"
                  : ""
              }
              ${
                notification.type === "info"
                  ? "border-blue-500 bg-blue-500/10"
                  : ""
              }
            `}
          >
            {notification.type === "error" && (
              <AlertCircle className="h-4 w-4" />
            )}
            <AlertDescription>{notification.message}</AlertDescription>
          </Alert>
        )}

        {/* Page Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              Schools & Campuses
            </h1>
            <p className="text-muted-foreground">
              Manage educational institutions and their campus locations
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="default"
              onClick={() => {
                refetchSchools();
                refetchCampuses();
              }}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="default">
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Add New
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Create New</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setIsCreateDialogOpen(true)}>
                  <GraduationCap className="mr-2 h-4 w-4" />
                  New School
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    // `selectedSchool` is page-level state left behind by
                    // "Add Campus"/"Manage Campuses" on a school card. Without
                    // clearing it, this generic entry point silently creates the
                    // campus under whichever school was last opened.
                    setSelectedSchool(null);
                    setIsCreateCampusDialogOpen(true);
                  }}
                >
                  <Building2 className="mr-2 h-4 w-4" />
                  New Campus
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid gap-4 md:grid-cols-5">
          <StatCard
            title="Total Schools"
            value={stats.totalSchools}
            icon={<GraduationCap className="w-4 h-4" />}
            variant="default"
          />
          <StatCard
            title="Active Schools"
            value={stats.activeSchools}
            icon={<CheckCircle className="w-4 h-4" />}
            variant="green"
          />
          <StatCard
            title="Total Campuses"
            value={stats.totalCampuses}
            icon={<Building2 className="w-4 h-4" />}
            variant="blue"
          />
          <StatCard
            title="Active Campuses"
            value={stats.activeCampuses}
            icon={<CheckCircle className="w-4 h-4" />}
            variant="green"
          />
          <StatCard
            title="Inactive Items"
            value={
              stats.inactiveSchools +
              (stats.totalCampuses - stats.activeCampuses)
            }
            icon={<XCircle className="w-4 h-4" />}
            variant="red"
          />
        </div>

        {/* Main Content Tabs */}
        <Tabs value={selectedTab} onValueChange={setSelectedTab}>
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="schools">
              Schools ({stats.totalSchools})
            </TabsTrigger>
            <TabsTrigger value="campuses">
              Campuses ({stats.totalCampuses})
            </TabsTrigger>
          </TabsList>

          {/* Schools Tab */}
          <TabsContent value="schools" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Schools Management</CardTitle>
                <CardDescription>
                  View and manage all registered schools in the system
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Search and Filters */}
                <SearchFilter
                  searchTerm={searchTerm}
                  onSearchChange={setSearchTerm}
                  placeholder="Search schools by name or domain..."
                  filters={[
                    {
                      label: "Status",
                      value: statusFilter,
                      onChange: setStatusFilter,
                      options: [
                        { label: "All Schools", value: "all" },
                        { label: "Active Only", value: "active" },
                        { label: "Inactive Only", value: "inactive" },
                      ],
                    },
                  ]}
                  activeFiltersCount={activeFiltersCount}
                  onClearFilters={() => setStatusFilter("all")}
                />

                {/* Schools Table */}
                <div className="mt-6 rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>School Name</TableHead>
                        <TableHead>Display Name</TableHead>
                        <TableHead>Email Domain</TableHead>
                        <TableHead>Campuses</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredSchools.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="h-24 text-center">
                            <div className="flex flex-col items-center justify-center">
                              <GraduationCap className="w-8 h-8 text-muted-foreground mb-2" />
                              <p className="text-muted-foreground">
                                {searchTerm || statusFilter !== "all"
                                  ? "No schools found matching your criteria"
                                  : "No schools available"}
                              </p>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredSchools.map((school: School) => (
                          <TableRow
                            key={school._id}
                            className="hover:bg-muted/50"
                          >
                            <TableCell className="font-medium">
                              <TruncatedTableCell
                                text={school.school_name}
                                maxLength={30}
                              />
                            </TableCell>
                            <TableCell>
                              <TruncatedTableCell
                                text={school.display_name}
                                maxLength={30}
                              />
                            </TableCell>
                            <TableCell>
                              <TruncatedTableCell
                                text={school.email_domain}
                                maxLength={25}
                              />
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Badge
                                  variant="secondary"
                                  className="font-normal"
                                >
                                  {school.campuses?.length || 0}
                                </Badge>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedSchool(school);
                                    setIsManageCampusesDialogOpen(true);
                                  }}
                                >
                                  <Building2 className="w-4 h-4 mr-1" />
                                  Manage
                                </Button>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant={
                                  school.is_active ? "default" : "secondary"
                                }
                              >
                                {school.is_active ? "Active" : "Inactive"}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {format(
                                new Date(school.createdAt),
                                "MMM dd, yyyy",
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    <MoreHorizontal className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setSelectedSchool(school);
                                      setIsCreateCampusDialogOpen(true);
                                    }}
                                  >
                                    <PlusIcon className="mr-2 h-4 w-4" />
                                    Add Campus
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setSelectedSchool(school);
                                      setFormData({
                                        ...initialFormData,
                                        school_name: school.school_name,
                                        display_name: school.display_name,
                                        email_domain: school.email_domain,
                                        is_active: school.is_active,
                                      });
                                      setIsEditDialogOpen(true);
                                    }}
                                  >
                                    <PencilIcon className="mr-2 h-4 w-4" />
                                    Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setUserManagementEntity({
                                        type: "school",
                                        id: school._id,
                                        name: school.school_name,
                                      });
                                      setIsUserManagementDialogOpen(true);
                                    }}
                                  >
                                    <Users className="mr-2 h-4 w-4" />
                                    Manage Users
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => handleDeleteSchool(school)}
                                    className="text-destructive focus:text-destructive"
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Campuses Tab */}
          <TabsContent value="campuses" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Campus Management</CardTitle>
                <CardDescription>
                  View and manage all campus locations
                </CardDescription>
              </CardHeader>
              <CardContent>
                <SearchFilter
                  searchTerm={campusSearchTerm}
                  onSearchChange={setCampusSearchTerm}
                  placeholder="Search campuses by name or location..."
                />

                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mt-6">
                  {filteredCampuses.length === 0 ? (
                    <div className="col-span-full text-center py-12">
                      <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                      <p className="text-muted-foreground">
                        {campusSearchTerm
                          ? "No campuses found matching your search"
                          : "No campuses available"}
                      </p>
                    </div>
                  ) : (
                    filteredCampuses.map((campus: Campus) => (
                      <CampusCard
                        key={campus._id}
                        campus={campus}
                        isAttached={false}
                        onManageUsers={() => {
                          setUserManagementEntity({
                            type: "campus",
                            id: campus._id,
                            name: campus.campus_name,
                          });
                          setIsUserManagementDialogOpen(true);
                        }}
                        onEdit={() => handleEditCampus(campus)}
                        onDelete={() => handleDeleteCampus(campus)}
                        onSyncPlaces={() => handleSyncPlaces(campus._id)}
                        isSyncing={syncingCampusId === campus._id}
                      />
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Dialogs */}

        {/* Manage Campuses — a drawer, matching the school form. The dialog it
            replaced squeezed two columns of campus cards into a centred box,
            which forced a horizontal scrollbar. */}
        <Sheet
          open={isManageCampusesDialogOpen}
          onOpenChange={(open) => {
            setIsManageCampusesDialogOpen(open);
            if (!open) {
              setCampusAttachmentError(null); // Clear error when closing
            }
          }}
        >
          <SheetContent
            side="right"
            className="w-full sm:max-w-3xl lg:max-w-5xl flex flex-col p-0"
          >
            <SheetHeader className="p-6 pb-4 border-b">
              <SheetTitle className="text-2xl font-semibold flex items-center gap-2">
                <Building2 className="w-6 h-6" />
                Manage Campuses
              </SheetTitle>
              <SheetDescription>
                Attach or detach campus locations for{" "}
                <span className="font-medium text-foreground">
                  {selectedSchool?.school_name}
                </span>
              </SheetDescription>
            </SheetHeader>

            {campusAttachmentError && (
              <Alert variant="destructive" className="mx-6 mt-4">
                <XCircle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{campusAttachmentError}</AlertDescription>
              </Alert>
            )}

            <div className="flex-1 overflow-y-auto space-y-6 px-6 py-4">
              <SearchFilter
                searchTerm={campusSearchTerm}
                onSearchChange={setCampusSearchTerm}
                placeholder="Search campuses..."
              />

              {/* Attached Campuses */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  Attached Campuses ({filteredSchoolCampuses.length})
                </h3>
                {isLoadingSchoolCampuses ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin" />
                  </div>
                ) : filteredSchoolCampuses.length === 0 ? (
                  <Card className="border-dashed">
                    <CardContent className="flex flex-col items-center justify-center py-8">
                      <Building2 className="w-12 h-12 text-muted-foreground mb-3" />
                      <p className="text-muted-foreground text-center">
                        {campusSearchTerm && schoolCampuses.length > 0
                          ? "No attached campuses match your search"
                          : "No campuses attached to this school yet"}
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2">
                    {filteredSchoolCampuses.map((campus: Campus) => (
                      <CampusCard
                        key={campus._id}
                        campus={campus}
                        isAttached={true}
                        onDetach={() =>
                          handleDetachCampus(campus._id, campus.campus_name)
                        }
                        onManageUsers={() => {
                          setUserManagementEntity({
                            type: "campus",
                            id: campus._id,
                            name: campus.campus_name,
                          });
                          setIsUserManagementDialogOpen(true);
                        }}
                        onEdit={() => handleEditCampus(campus)}
                        onSyncPlaces={() => handleSyncPlaces(campus._id)}
                        isSyncing={syncingCampusId === campus._id}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Available Campuses */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Available Campuses ({filteredAvailableCampuses.length})
                </h3>
                {filteredAvailableCampuses.length === 0 ? (
                  <Card className="border-dashed">
                    <CardContent className="flex flex-col items-center justify-center py-8">
                      <Building2 className="w-12 h-12 text-muted-foreground mb-3" />
                      <p className="text-muted-foreground text-center">
                        {campusSearchTerm && availableCampuses.length > 0
                          ? "No available campuses match your search"
                          : "All campuses are already attached or none are available"}
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-4"
                        onClick={() => setIsCreateCampusDialogOpen(true)}
                      >
                        <PlusIcon className="w-4 h-4 mr-2" />
                        Create New Campus
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2">
                    {filteredAvailableCampuses.map((campus: Campus) => (
                      <CampusCard
                        key={campus._id}
                        campus={campus}
                        isAttached={false}
                        onAttach={async () => {
                          if (selectedSchool) {
                            try {
                              setCampusAttachmentError(null); // Clear any previous error
                              // Use the school's attach endpoint
                              await schoolsAPI.attachCampusToSchool(
                                selectedSchool._id,
                                campus._id,
                              );

                              // Refresh all data
                              queryClient.invalidateQueries({
                                queryKey: ["campuses"],
                              });
                              queryClient.invalidateQueries({
                                queryKey: ["school-campuses"],
                              });
                              queryClient.invalidateQueries({
                                queryKey: ["schools"],
                              });
                              showNotification(
                                "Campus attached successfully",
                                "success",
                              );
                            } catch (err) {
                              const error = err as ApiError;
                              const errorMessage =
                                error.response?.data?.message ||
                                "Failed to attach campus";
                              setCampusAttachmentError(errorMessage);
                              showNotification(errorMessage, "error");
                            }
                          }
                        }}
                        onEdit={() => handleEditCampus(campus)}
                        onSyncPlaces={() => handleSyncPlaces(campus._id)}
                        isSyncing={syncingCampusId === campus._id}
                        isLoading={false}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            <SheetFooter className="border-t p-6 flex-row justify-end">
              <SheetClose asChild>
                <Button variant="outline">Close</Button>
              </SheetClose>
            </SheetFooter>
          </SheetContent>
        </Sheet>

        {/* Create Campus — drawer, like the school form. The dialog it replaced
            was forced to 90vw/90vh with inline styles and !important classes to
            fit the map, which left it neither a dialog nor a full page. */}
        <Sheet
          open={isCreateCampusDialogOpen}
          onOpenChange={setIsCreateCampusDialogOpen}
        >
          <SheetContent
            side="right"
            className="w-full sm:max-w-3xl lg:max-w-5xl overflow-y-auto p-6"
          >
            <CampusForm
              title={
                selectedSchool
                  ? `Add Campus to ${selectedSchool.school_name}`
                  : "Create New Campus"
              }
              schoolId={selectedSchool?._id}
              // Standalone entry point has no school pinned, so the form has to
              // ask for one rather than let the backend guess.
              schools={schools}
              onSubmit={(data: CampusFormData) => {
                const campusData: Partial<Campus> = {
                  ...data,
                  coordinates: data.coordinates
                    ? {
                        type: "Polygon" as const,
                        coordinates: data.coordinates.coordinates,
                      }
                    : undefined,
                };
                createCampusMutation.mutate(campusData);
              }}
              onCancel={() => {
                setIsCreateCampusDialogOpen(false);
              }}
              isLoading={createCampusMutation.isPending}
              submitLabel="Create Campus"
            />
          </SheetContent>
        </Sheet>

        {/* Edit Campus — drawer, same treatment as create. */}
        <Sheet
          open={isEditCampusDialogOpen}
          onOpenChange={(open) => {
            setIsEditCampusDialogOpen(open);
            if (!open) {
              setSelectedCampus(null);
            }
          }}
        >
          <SheetContent
            side="right"
            className="w-full sm:max-w-3xl lg:max-w-5xl overflow-y-auto p-6"
          >
            {selectedCampus && (
              <CampusForm
                title={`Edit Campus - ${selectedCampus.campus_name}`}
                initialData={{
                  campus_name: selectedCampus.campus_name,
                  campus_short_name: selectedCampus.campus_short_name,
                  address: selectedCampus.address,
                  city: selectedCampus.city,
                  state: selectedCampus.state,
                  zip: selectedCampus.zip,
                  phone_number: selectedCampus.phone_number,
                  time_zone: selectedCampus.time_zone,
                  is_active: selectedCampus.is_active,
                  image_url: selectedCampus.image_url,
                  coordinates: selectedCampus.coordinates,
                  dawn_to_dusk: selectedCampus.dawn_to_dusk,
                }}
                onSubmit={(data: CampusFormData) => {
                  const campusData: Partial<Campus> = {
                    ...data,
                    coordinates: data.coordinates
                      ? {
                          type: "Polygon" as const,
                          coordinates: data.coordinates.coordinates,
                        }
                      : undefined,
                  };
                  updateCampusMutation.mutate({
                    id: selectedCampus._id,
                    data: campusData,
                  });
                }}
                onCancel={() => {
                  setIsEditCampusDialogOpen(false);
                  setSelectedCampus(null);
                }}
                isLoading={updateCampusMutation.isPending}
                submitLabel="Update Campus"
              />
            )}
          </SheetContent>
        </Sheet>

        {/* Edit School Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit School</DialogTitle>
              <DialogDescription>
                Update the school information
              </DialogDescription>
            </DialogHeader>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (selectedSchool) {
                  updateSchoolMutation.mutate({
                    id: selectedSchool._id,
                    data: formData,
                  });
                }
              }}
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label htmlFor="edit_school_name">School Name</Label>
                <Input
                  id="edit_school_name"
                  value={formData.school_name}
                  onChange={(e) =>
                    setFormData({ ...formData, school_name: e.target.value })
                  }
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_display_name">Display Name</Label>
                <Input
                  id="edit_display_name"
                  value={formData.display_name}
                  onChange={(e) =>
                    setFormData({ ...formData, display_name: e.target.value })
                  }
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_email_domain">Email Domain</Label>
                <Input
                  id="edit_email_domain"
                  value={formData.email_domain}
                  onChange={(e) =>
                    setFormData({ ...formData, email_domain: e.target.value })
                  }
                  required
                />
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="is_active_edit"
                  checked={formData.is_active}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, is_active: checked })
                  }
                />
                <Label htmlFor="is_active_edit">School is active</Label>
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline" type="button">
                    Cancel
                  </Button>
                </DialogClose>
                <Button type="submit" disabled={updateSchoolMutation.isPending}>
                  {updateSchoolMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    "Update School"
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Create School Dialog */}
        <Sheet open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <SheetContent
            side="right"
            className="w-full sm:max-w-3xl lg:max-w-5xl overflow-y-auto p-6"
          >
            <SheetHeader className="p-0">
              <SheetTitle className="text-2xl font-semibold flex items-center gap-2">
                <GraduationCap className="w-6 h-6" />
                Create New School
              </SheetTitle>
              <SheetDescription>
                Add a new educational institution to the system
              </SheetDescription>
            </SheetHeader>
            <form
              onSubmit={async (e) => {
                e.preventDefault();

                // Guard against a second submit slipping through before React
                // has re-rendered the disabled button.
                if (isCreatingSchool) return;
                setIsCreatingSchool(true);

                // Extract school data (without campus fields)
                const schoolData = {
                  school_name: formData.school_name,
                  display_name: formData.display_name,
                  email_domain: formData.email_domain,
                  is_active: formData.is_active,
                };

                try {
                  // Create school first. Anything other than a 2xx now throws,
                  // so a rejected create no longer falls through to the success
                  // path below.
                  const createdSchool =
                    await schoolsAPI.createSchool(schoolData);

                  // Always create campus with school
                  if (createdSchool) {
                    // Campus name is a required input, so this is only
                    // reachable defensively. Say what actually happened: a
                    // school with no campus is not usable in the app.
                    if (!formData.campusName.trim()) {
                      queryClient.invalidateQueries({ queryKey: ["schools"] });
                      setIsCreateDialogOpen(false);
                      showNotification(
                        "School created, but it has no campus yet. Add one before students can use it.",
                        "error",
                      );
                      resetFormData();
                      return;
                    }

                    // Validate required campus fields when creating campus
                    if (
                      !formData.campusAddress ||
                      !formData.campusCity ||
                      !formData.campusState ||
                      !formData.campusZip ||
                      !formData.campusPhone
                    ) {
                      throw new Error(
                        "Please fill in all required campus fields (Address, City, State, ZIP, Phone)",
                      );
                    }

                    // Validate campus boundary is required
                    if (!campusBoundaryData) {
                      throw new Error(
                        "Please draw a boundary for the campus on the map",
                      );
                    }

                    // Try different possible ID fields
                    const schoolId =
                      createdSchool._id ||
                      createdSchool.id ||
                      createdSchool.schoolId;

                    if (!schoolId) {
                      throw new Error(
                        "The school was created but the server did not return its ID, so the campus could not be linked. Add the campus from the school card.",
                      );
                    }

                    // The campus MUST carry school_id. Omitting it made the
                    // backend fall back to `req.user.school_id`, so the campus
                    // was created attached to the admin's own school and the
                    // follow-up attach then failed with 409 "already attached
                    // to another school" — leaving a stray campus behind on
                    // every single school creation.
                    const campusData: CampusCreateData = {
                      campus_name: formData.campusName,
                      address: formData.campusAddress,
                      city: formData.campusCity,
                      state: formData.campusState,
                      zip: formData.campusZip,
                      phone_number: formData.campusPhone,
                      is_active: true,
                      time_zone: formData.campusTimeZone,
                      dawn_to_dusk: [360, 1200], // 6 AM to 8 PM
                      school_id: schoolId,
                      seed_default_content: formData.seedDefaultContent,
                    };

                    // Add boundary coordinates (required)
                    campusData.coordinates = {
                      type: "Polygon",
                      coordinates: campusBoundaryData.geometry.coordinates,
                    };

                    try {
                      const createdCampus =
                        await campusAPI.createCampus(campusData);

                      // Try multiple possible ID fields
                      const campusId =
                        createdCampus._id ||
                        createdCampus.id ||
                        createdCampus.campusId ||
                        createdCampus.campus_id ||
                        (createdCampus.data && createdCampus.data._id) ||
                        (createdCampus.data && createdCampus.data.id) ||
                        (createdCampus.campus && createdCampus.campus._id) ||
                        (createdCampus.campus && createdCampus.campus.id);

                      if (!campusId) {
                        throw new Error(
                          "The campus was created but the server did not return its ID.",
                        );
                      }

                      // No attach call: POST /campus already pushes the new
                      // campus into the school's campuses[] when school_id is
                      // present. Calling attach afterwards would come back 409
                      // "already attached to this school" and be reported as a
                      // failure. Two writes became one, so there is also no
                      // longer a window where a campus is left orphaned.

                      // Success - close modal and refresh data
                      queryClient.invalidateQueries({ queryKey: ["schools"] });
                      queryClient.invalidateQueries({ queryKey: ["campuses"] });
                      setIsCreateDialogOpen(false);
                      resetFormData();
                      showNotification(
                        "School and campus created and linked successfully! 🎉",
                        "success",
                      );
                      return; // Important: return here to avoid executing the general success block
                    } catch (campusErr) {
                      const campusError = campusErr as ApiError;
                      console.error(
                        "Campus creation/attachment failed:",
                        campusError,
                      );

                      // Close modal and refresh data even with campus error
                      queryClient.invalidateQueries({ queryKey: ["schools"] });
                      queryClient.invalidateQueries({ queryKey: ["campuses"] });
                      setIsCreateDialogOpen(false);
                      resetFormData();

                      // The school exists but has no campus, which leaves it
                      // unusable in the app — say so rather than calling it a
                      // partial success.
                      showNotification(
                        `School created, but the campus failed: ${
                          campusError.response?.data?.message ||
                          campusError.message ||
                          "unknown error"
                        }. Add a campus from the school card before students can use it.`,
                        "error",
                      );
                      return;
                    }
                  }

                  // Success
                  queryClient.invalidateQueries({ queryKey: ["schools"] });
                  queryClient.invalidateQueries({ queryKey: ["campuses"] });
                  setIsCreateDialogOpen(false);
                  showNotification(
                    "School and campus created successfully",
                    "success",
                  );
                  resetFormData();
                } catch (err) {
                  const error = err as ApiError;
                  showNotification(
                    error.response?.data?.message ||
                      error.message ||
                      "Failed to create school",
                    "error",
                  );
                } finally {
                  // Every branch above returns from inside the try, so the flag
                  // has to be cleared here or the form stays locked.
                  setIsCreatingSchool(false);
                }
              }}
              className="space-y-6"
            >
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="school_name">School Name *</Label>
                    <Input
                      id="school_name"
                      value={formData.school_name}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          school_name: e.target.value,
                        })
                      }
                      required
                      placeholder="University of Example"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="display_name">Display Name *</Label>
                    <Input
                      id="display_name"
                      value={formData.display_name}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          display_name: e.target.value,
                        })
                      }
                      required
                      placeholder="UoE"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email_domain">Email Domain *</Label>
                  <Input
                    id="email_domain"
                    value={formData.email_domain}
                    onChange={(e) =>
                      setFormData({ ...formData, email_domain: e.target.value })
                    }
                    placeholder="example.edu"
                    required
                  />
                  <p className="text-sm text-muted-foreground">
                    Students with this email domain will be associated with this
                    school
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_active_create"
                    checked={formData.is_active}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, is_active: checked })
                    }
                  />
                  <Label htmlFor="is_active_create">School is active</Label>
                </div>

                {/* Campus Creation Section */}
                <div className="border-t pt-4">
                  <div className="mb-4">
                    <Label className="flex items-center gap-2 text-base font-medium">
                      <Building2 className="w-4 h-4" />
                      Main Campus Information
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Every school needs a main campus. Fill in the campus
                      details below.
                    </p>
                  </div>

                  {/* Sent as `seed_default_content` on the campus create call.
                      Leaving it on means the campus opens with a welcome space,
                      event and idea instead of empty. The copy is editable
                      under Administration > Default Content. */}
                  <div className="flex items-start justify-between gap-4 mb-4 rounded-lg border p-4">
                    <div>
                      <Label
                        htmlFor="seed_default_content"
                        className="text-sm font-medium"
                      >
                        Add Walky welcome content
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        Creates a welcome space, a recurring welcome event and an
                        opening idea on this campus, owned by the official Walky
                        account.
                      </p>
                    </div>
                    <Switch
                      id="seed_default_content"
                      checked={formData.seedDefaultContent}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, seedDefaultContent: checked })
                      }
                    />
                  </div>

                  <div className="space-y-4 bg-muted/30 p-4 rounded-lg">
                    <div className="space-y-4 bg-muted/30 p-4 rounded-lg">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="campus_name">Campus Name *</Label>
                          <Input
                            id="campus_name"
                            value={formData.campusName || ""}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                campusName: e.target.value,
                              })
                            }
                            placeholder="Main Campus"
                            required={formData.createCampusWithSchool}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="campus_address">
                            Campus Address *
                          </Label>
                          <Input
                            id="campus_address"
                            value={formData.campusAddress || ""}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                campusAddress: e.target.value,
                              })
                            }
                            placeholder="123 University Ave"
                            required={formData.createCampusWithSchool}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="campus_city">City *</Label>
                          <Input
                            id="campus_city"
                            value={formData.campusCity || ""}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                campusCity: e.target.value,
                              })
                            }
                            placeholder="New York"
                            required={formData.createCampusWithSchool}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="campus_state">State *</Label>
                          <Input
                            id="campus_state"
                            value={formData.campusState || ""}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                campusState: e.target.value,
                              })
                            }
                            placeholder="NY"
                            required={formData.createCampusWithSchool}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="campus_zip">ZIP Code *</Label>
                          <Input
                            id="campus_zip"
                            value={formData.campusZip || ""}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                campusZip: e.target.value,
                              })
                            }
                            placeholder="10001"
                            required={formData.createCampusWithSchool}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="campus_phone">Campus Phone *</Label>
                        <Input
                          id="campus_phone"
                          value={formData.campusPhone || ""}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              campusPhone: e.target.value,
                            })
                          }
                          placeholder="+1 (555) 123-4567"
                          required={formData.createCampusWithSchool}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="campus_timezone">Time Zone *</Label>
                        <Select
                          value={formData.campusTimeZone}
                          onValueChange={(value) =>
                            setFormData({ ...formData, campusTimeZone: value })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select time zone" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="America/New_York">
                              America/New York
                            </SelectItem>
                            <SelectItem value="America/Chicago">
                              America/Chicago
                            </SelectItem>
                            <SelectItem value="America/Denver">
                              America/Denver
                            </SelectItem>
                            <SelectItem value="America/Los_Angeles">
                              America/Los Angeles
                            </SelectItem>
                            <SelectItem value="America/Anchorage">
                              America/Anchorage
                            </SelectItem>
                            <SelectItem value="Pacific/Honolulu">
                              Pacific/Honolulu
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="pt-4">
                        <Label className="text-sm font-medium mb-2 block">
                          Campus Boundary *
                        </Label>
                        <CampusBoundary
                          initialBoundaryData={campusBoundaryData}
                          onBoundaryChange={(boundary) => {
                            setCampusBoundaryData(boundary);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <SheetFooter className="p-0 flex-row justify-end gap-2">
                <SheetClose asChild>
                  <Button variant="outline" type="button">
                    Cancel
                  </Button>
                </SheetClose>
                <Button type="submit" disabled={isCreatingSchool}>
                  {isCreatingSchool ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    "Create School"
                  )}
                </Button>
              </SheetFooter>
            </form>
          </SheetContent>
        </Sheet>

        {/* Confirmation Dialog */}
        <ConfirmationDialog
          isOpen={confirmDialog.isOpen}
          onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
          onConfirm={confirmDialog.onConfirm}
          title={confirmDialog.title}
          description={confirmDialog.description}
          variant={confirmDialog.variant}
          confirmText="Confirm"
          cancelText="Cancel"
        />

        {/* User Management Dialog */}
        {userManagementEntity && (
          <UserManagementDialog
            isOpen={isUserManagementDialogOpen}
            onClose={() => {
              setIsUserManagementDialogOpen(false);
              setUserManagementEntity(null);
            }}
            entityType={userManagementEntity.type}
            entityId={userManagementEntity.id}
            entityName={userManagementEntity.name}
            availableCampuses={
              userManagementEntity.type === "school" ? schoolCampuses : []
            }
          />
        )}
      </div>
    </DashboardLayout>
  );
}
